<script lang="ts">
    import { faPencil, faPlusCircle, faTrash, faLink, faPrint, faSignature, faUser, faEye } from '@fortawesome/free-solid-svg-icons'
    import { onMount } from 'svelte/internal'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import DateSelect from '../../_common/date-select.svelte'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Ztab from '../../_common/z/ztab.svelte'
    import Popover from '../../_common/popover.svelte'
    import { GridBadge, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import FormV2FormEditor from './form-v2-form-editor.svelte'
    import FormV2FormLinkPopover from './form-v2-form-link-popover.svelte'
    import FormV2SignPopover from './form-v2-sign-popover.svelte'
    import { printFormV2 } from './form-v2-print.helper'
    import type { FormV2Form } from '../../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'
    import type { FormData } from '../../../modules/toolsApi/forms/form-datas/form-data'

    const DRAFT = 60
    const COMPLETED = 100
    const SIGNING = 105
    const SIGNED = 110

    const tabs = [{ title: 'Atendimento', url: () => {} }]
    let activeTab = 'Atendimento'

    let step = 1
    let show = false
    let linkFormId: string | null = null
    let linkFormCompleted = false
    let signFormId: string | null = null

    // listagem
    let entities: Pick<
        FormV2Form,
        | 'formV2FormId'
        | 'formV2TemplateId'
        | 'title'
        | 'personId'
        | 'personName'
        | 'attendantName'
        | 'status'
        | 'signatureRequested'
        | 'createdAt'
        | 'updatedAt'
    >[] = []
    let grid: GridClass = new GridClass()
    let dates: { from: Date; to: Date } = window.api.utils.dates.today()

    // editor
    let editingFormV2FormId: string | null = null
    let editingView = false

    // Documentos enviados pela versão anterior (v1) — mantidos visíveis por um tempo aqui embaixo
    // enquanto a migração pro v2 não é 100% concluída (ver customer-service-entries.svelte, mesmo
    // padrão de grid+DateSelect da tela dedicada da versão legada).
    let legacyEntities: FormData[] = []
    let legacyGrid: GridClass = new GridClass()
    let legacyDates: { from: Date; to: Date } = window.api.utils.dates.today()

    onMount(async () => {
        await Promise.all([loadEntities(), loadLegacyEntities()])
    })

    async function loadEntities() {
        try {
            show = true
            entities = await window.api.tools.formsV2.forms.getAll({
                from: dates?.from || undefined,
                to: dates?.to || undefined,
            })
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function dateChanged(event: CustomEvent<{ from: Date; to: Date }>) {
        dates = event.detail
        legacyDates = event.detail
        await Promise.all([loadEntities(), loadLegacyEntities()])
    }

    function getStatusLabel(status: number) {
        if (status === DRAFT) return 'Preenchendo'
        if (status === COMPLETED) return 'Concluído'
        if (status === SIGNING) return 'Assinando'
        if (status === SIGNED) return 'Assinado'
        return 'Desconhecido'
    }

    function loadGrid() {
        grid = new GridClass()
        grid.title = 'Atendimentos'
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Formulário', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Pessoa', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime, size: 2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))

        for (const item of entities) {
            const line = new GridLine()
            line.slots.push({
                content: new GridBadge({ label: getStatusLabel(item.status), class: 'status_' + item.status }),
            })
            line.slots.push({ content: item.title })
            line.slots.push({ content: item.personName || '—' })
            line.slots.push({ content: item.createdAt })

            const locked = item.status >= SIGNING
            const buttons: GridButton[] = []
            if (item.status === DRAFT) {
                buttons.push(new GridButton({ icon: faPencil, label: 'Editar', callback: () => openEdit(item.formV2FormId, false) }))
            } else {
                buttons.push(new GridButton({ icon: faEye, label: 'Ver', callback: () => openEdit(item.formV2FormId, true) }))
            }
            buttons.push(new GridButton({ icon: faUser, label: 'Pessoa', callback: () => window.person('show', item.personId) }))
            buttons.push(new GridButton({ icon: faLink, label: 'Link', callback: () => openShare(item.formV2FormId, item.status) }))
            buttons.push(new GridButton({ icon: faPrint, label: 'Imprimir', callback: () => printEntity(item.formV2FormId) }))
            // Iniciar assinatura só é possível em modelos com "Solicitar assinatura ao preencher" — sem
            // isso o backend recusaria o start, então o botão não aparece. Já em documentos que estão
            // ou passaram pela assinatura o botão permanece independente da regra: é por ele que se
            // baixa o documento assinado, o relatório e o histórico, inclusive nos que foram assinados
            // antes desta regra existir.
            if (item.status >= SIGNING || (item.status === COMPLETED && item.signatureRequested)) {
                buttons.push(new GridButton({ icon: faSignature, label: 'Assinatura', callback: () => openSign(item.formV2FormId) }))
            }
            if (!locked) buttons.push(new GridButton({ icon: faTrash, label: 'Excluir', callback: () => removeEntity(item.formV2FormId) }))
            line.slots.push({ content: buttons })
            grid.lines.push(line)
        }
    }

    function getLegacyStatusLabel(status: number) {
        if (status === DRAFT) return 'Rascunho'
        if (status === SIGNED) return 'Assinado'
        return 'Completo'
    }

    async function loadLegacyEntities() {
        try {
            legacyEntities = await window.api.tools.forms.formDatas.getAll({
                from: legacyDates?.from || undefined,
                to: legacyDates?.to || undefined,
            })
            legacyEntities = await window.api.utils.utils.assignPersonsFromObject(legacyEntities)
            loadLegacyGrid()
        } catch (error) {
            window.error(error)
        }
    }

    function legacyDateChanged(event: CustomEvent<{ from: Date; to: Date }>) {
        legacyDates = event.detail
        loadLegacyEntities()
    }

    function loadLegacyGrid() {
        legacyGrid = new GridClass()
        legacyGrid.title = 'Atendimentos (versão legada)'
        legacyGrid.subtitle = ''
        legacyGrid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        legacyGrid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime, size: 2 }))
        legacyGrid.cols.push(new GridCol({ content: 'Pessoa', type: GridColTypeEnum.string, maxLength: 33 }))
        legacyGrid.cols.push(new GridCol({ content: 'Modelo', type: GridColTypeEnum.string, maxLength: 45 }))
        legacyGrid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))

        for (const item of legacyEntities) {
            const line = new GridLine()
            line.slots.push({
                content: new GridBadge({ label: getLegacyStatusLabel(item.status), class: 'status_' + item.status }),
            })
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: (item as any).person?.name || '[cliente não informado]' })
            line.slots.push({ content: item.template?.title })

            const buttons: GridButton[] = [new GridButton({ icon: faUser, label: 'Pessoa', callback: () => window.person('show', item.personId) })]
            if (window.store.token.permissions.customerService.entries.read) {
                buttons.push(new GridButton({ icon: faEye, label: 'Ver', callback: () => showLegacyEntity(item.formDataId) }))
            }
            if (window.store.token.permissions.customerService.entries.write) {
                buttons.push(new GridButton({ icon: faPencil, label: 'Editar', callback: () => editLegacyEntity(item.formDataId) }))
            }
            line.slots.push({ content: buttons })
            legacyGrid.lines.push(line)
        }
        legacyGrid = legacyGrid
    }

    function newLegacyEntity() {
        window.popover({ act: 'customer-service', data: { view: false }, callback: () => loadLegacyEntities() })
    }

    function editLegacyEntity(id: string) {
        window.popover({ act: 'customer-service', data: { formDataId: id, view: false }, callback: () => loadLegacyEntities() })
    }

    function showLegacyEntity(id: string) {
        window.popover({ act: 'customer-service', data: { formDataId: id, view: true }, callback: () => loadLegacyEntities() })
    }

    function openNew() {
        editingFormV2FormId = null
        editingView = false
        step = 2
    }

    function openEdit(id: string, viewOnly: boolean = false) {
        editingFormV2FormId = id
        editingView = viewOnly
        step = 2
    }

    function openShare(id: string, status: number) {
        linkFormId = id
        linkFormCompleted = status === COMPLETED
    }

    function openSign(id: string) {
        signFormId = id
    }

    async function closeSign() {
        signFormId = null
        await loadEntities()
    }

    async function printEntity(id: string) {
        try {
            show = true
            await printFormV2(id)
        } finally {
            show = false
        }
    }

    async function removeEntity(id: string) {
        if (!confirm('Deseja mesmo excluir este preenchimento?')) return
        try {
            show = true
            await window.api.tools.formsV2.forms.remove(id)
            await loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function onEditorChange(event: CustomEvent<{ formV2FormId: string; openLink?: boolean; completed?: boolean }>) {
        step = 1
        await loadEntities()
        if (event.detail?.openLink) {
            openShare(event.detail.formV2FormId, event.detail.completed ? COMPLETED : DRAFT)
        }
    }

    function onEditorCancel() {
        step = 1
    }
</script>

<div class="root-content">
    <Ztab class="h-space" {tabs} active={activeTab} />
    <Content footer={true}>
        {#if step === 1}
            <Zact>
                <DateSelect shortcuts={true} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
                <div class="spacer" />
                <Zbutton color="primary" icon={faPlusCircle} label="Novo atendimento" mobileHideLabel={true} on:click={openNew} />
            </Zact>

            <Zgrid {grid} class="h-space" />

            {#if legacyGrid.lines.length > 0}
                <Zgrid grid={legacyGrid} class="h-space" />
            {/if}
        {:else}
            <FormV2FormEditor formV2FormId={editingFormV2FormId} view={editingView} on:change={onEditorChange} on:cancel={onEditorCancel} />
        {/if}
        <LoadSpinner {show} />
    </Content>
</div>

{#if linkFormId}
    <Popover title="Gerar link público" width="580px" on:close={() => (linkFormId = null)}>
        <FormV2FormLinkPopover formV2FormId={linkFormId} completed={linkFormCompleted} />
    </Popover>
{/if}

{#if signFormId}
    <Popover title="Assinatura do documento" width="740px" on:close={closeSign}>
        <FormV2SignPopover formV2FormId={signFormId} on:refresh={loadEntities} />
    </Popover>
{/if}
